import { useRef, useState } from "react";
import { Camera, Loader2, Trash2 } from "lucide-react";

// Reusable photo uploader: phone/tablet camera capture → object storage → attached to a record.
export function PhotoUpload({ api, attachUrl, photos = [], onChange, tags = ["General"] }) {
  const fileRef = useRef(null);
  const [tag, setTag] = useState(tags[0]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    setError("");
    try {
      const form = new FormData();
      form.append("file", file);
      const { data: up } = await api.post("/uploads", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await api.post(attachUrl, { path: up.path, tag, filename: file.name });
      onChange?.();
    } catch (e) {
      setError(e.response?.data?.detail || "Upload failed — try again");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async (path) => {
    try {
      await api.post("/files/delete", { path });
      onChange?.();
    } catch {}
  };

  return (
    <div data-testid="photo-upload">
      <div className="flex items-center gap-2 mb-2.5">
        <select
          value={tag}
          onChange={(e) => setTag(e.target.value)}
          aria-label="Photo label"
          className="rounded-lg border border-warm-300 px-2.5 py-2 text-xs focus:border-navy-900 focus:outline-none"
        >
          {tags.map((t) => (
            <option key={t}>{t}</option>
          ))}
        </select>
        <button
          type="button"
          data-testid="photo-upload-btn"
          disabled={busy}
          onClick={() => fileRef.current?.click()}
          className="inline-flex items-center gap-1.5 rounded-full bg-navy-900 px-4 py-2 text-xs font-semibold text-white hover:bg-navy-800 disabled:opacity-40"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />}
          Add Photo
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => upload(e.target.files?.[0])}
          aria-label="Take or choose a photo"
        />
      </div>
      {error && <p role="alert" className="text-xs text-red-700 mb-2">{error}</p>}
      {photos.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {photos.map((p) => (
            <figure key={p.path} className="relative group">
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}/api/portal/files/${p.path}`}
                alt={p.tag || "Uploaded photo"}
                loading="lazy"
                className="h-20 w-full rounded-lg object-cover border border-warm-200"
              />
              <figcaption className="text-[10px] text-slate-500 mt-1 truncate">{p.tag}</figcaption>
              <button
                type="button"
                aria-label="Delete photo"
                onClick={() => remove(p.path)}
                className="absolute top-1 right-1 rounded-full bg-white/90 p-1 text-slate-500 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </figure>
          ))}
        </div>
      )}
    </div>
  );
}
